import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tabs,
  Tab,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import DraggableTreeNode from '../../components/DraggableTreeNode';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';

import logoTachafy from '../../assets/logo TACHAFY 1.png';
import QuestionTreeIconAdmin from '../../assets/QuestionTreeIconAdmin.png';
import OverviewListIcon from '../../assets/images/overview-list-item.svg';
import OverviewListIcon2 from '../../assets/images/overview-list-icon-2.svg';

import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import DatabaseService from '../../services/DatabaseService';

import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DoctorSection from 'components/ModelTestingAdmin/DoctorsTesting/DoctorSection';
import PartnerSection from 'components/SuperAdmin/Partners/PartnerSection';
import ReviewsTabs from '../../components/ModelTestingAdmin/ReviewsLists/ReviewsTabs';
import StatisticsTabs from '../../components/ModelTestingAdmin/Statistics/StatisticsTabs';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';



interface Option {
  text: string;
  nextQuestion: string;
  recommendations: string[];
}

interface Question {
  id: string;
  question: string;
  options: Option[];
}

interface TreeNodePosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface TreeNodeData extends Question {
  position?: TreeNodePosition;
  children?: TreeNodeData[];
  isReference?: boolean;
}

interface DatabaseOption {
  text: string;
  nextQuestion?: string;
  recommendations?: string[];
}

interface DatabaseQuestion {
  id: string;
  question: string;
  options: DatabaseOption[];
}

interface DatabaseResponse {
  questions: DatabaseQuestion[];
}


const LEVEL_HEIGHT = 150;
const NODE_WIDTH = 300;
const NODE_HEIGHT = 100;
const NODE_SPACING = 60;

const GRID_SIZE = 20;
const GRID_COLOR = '#f0f0f0';

// Initialize empty option with default values
const emptyOption: Option = {
  text: '',
  nextQuestion: '',
  recommendations: []
};

// Convert database option to application option
const convertDatabaseOption = (opt: DatabaseOption): Option => ({
  text: opt.text || '',
  nextQuestion: opt.nextQuestion || '',
  recommendations: opt.recommendations || []
});

// Convert application option to database option
const convertToDatabaseOption = (opt: Option): DatabaseOption => ({
  text: opt.text,
  nextQuestion: opt.nextQuestion || undefined,
  recommendations: opt.recommendations
});

// Convert database question to application question
const convertDatabaseQuestion = (question: DatabaseQuestion): Question => ({
  id: question.id,
  question: question.question,
  options: question.options.map(convertDatabaseOption)
});

// Convert application question to database question
const convertToDatabaseQuestion = (question: Question): DatabaseQuestion => ({
  id: question.id,
  question: question.question,
  options: question.options.map(convertToDatabaseOption)
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Add this helper function near the top with other utility functions
const generateQuestionId = (questionText: string): string => {
  // Convert the question text to lowercase and remove special characters
  const cleanText = questionText
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_');

  // Take first 3 words maximum
  const shortText = cleanText.split('_').slice(0, 3).join('_');

  // Add prefix and timestamp to ensure uniqueness
  return `q_${shortText}_${Date.now().toString(36)}`;
};

const generateUniqueQuestionId = (baseId: string, existingIds: Set<string>): string => {
  // Remove any existing copy numbers from the base ID
  const cleanBaseId = baseId.replace(/_copy_\d+$/, '');

  // Start with copy_1, increment until we find a unique ID
  let copyNumber = 1;
  let newId = `${cleanBaseId}_copy_${copyNumber}`;

  while (existingIds.has(newId)) {
    copyNumber++;
    newId = `${cleanBaseId}_copy_${copyNumber}`;
  }

  return newId;
};

const ModelTesting = () => {
  const [questionsData, setQuestionsData] = useState<Question[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [newQuestion, setNewQuestion] = useState<Question>({ id: '', question: '', options: [] });
  const [editingOption, setEditingOption] = useState<Option & { index?: number }>(emptyOption);
  const [treeData, setTreeData] = useState<TreeNodeData[]>([]);
  const svgRef = useRef<SVGSVGElement>(null);
  const [scale, setScale] = useState(1);
  const [linkMode, setLinkMode] = useState(false);
  const [linkStart, setLinkStart] = useState<string | null>(null);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [isEditingExistingOption, setIsEditingExistingOption] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { logout } = useAuth();
  const navigate = useNavigate();

  // Add these constants at the top with other constants
  const MIN_NODE_SPACING = 60;  // Minimum horizontal space between nodes
  const LEVEL_SPACING = 150;    // Vertical space between levels
  const PADDING = 50;           // Padding around the entire tree

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const response: DatabaseResponse = await DatabaseService.getQuestions();
        const questions = response.questions.map(convertDatabaseQuestion);
        setQuestionsData(questions);
      } catch (e) {
        console.error('Error loading questions:', e);
      }
    };
    loadQuestions();
  }, []);

  useEffect(() => {
    // Build tree structure
    const buildTree = (questions: Question[], startId: string, visited = new Set<string>()): TreeNodeData | null => {
      // If we've already visited this node, don't process it again
      if (visited.has(startId)) {
        // Return a reference node instead of null
        const question = questions.find(q => q.id === startId);
        if (question) {
          return {
            ...question,
            children: [], // Empty children to prevent cycles
            isReference: true // Mark as a reference node
          };
        }
        return null;
      }

      const question = questions.find(q => q.id === startId);
      if (!question) return null;

      // Add to visited set before processing children
      visited.add(startId);

      const node: TreeNodeData = {
        ...question,
        children: []
      };

      // Create a map to track unique next questions
      const uniqueNextQuestions = new Map<string, boolean>();

      // Process all children, but only add unique ones
      question.options.forEach(option => {
        if (option.nextQuestion && !uniqueNextQuestions.has(option.nextQuestion)) {
          uniqueNextQuestions.set(option.nextQuestion, true);
          const child = buildTree(questions, option.nextQuestion, new Set(visited));
          if (child) {
            node.children?.push(child);
          }
        }
      });

      return node;
    };

    const findRootQuestions = (questions: Question[]): TreeNodeData[] => {
      const allNextQuestions = new Set(
        questions.flatMap(q => q.options.map(o => o.nextQuestion)).filter(Boolean)
      );

      // Find all questions that are not referenced as next questions
      const rootQuestions = questions.filter(q => !allNextQuestions.has(q.id));

      // Create a new visited set for each root question
      return rootQuestions
        .map(q => buildTree(questions, q.id, new Set()))
        .filter((node): node is TreeNodeData => node !== null);
    };

    const calculatePositions = (nodes: TreeNodeData[], level = 0, parentX = 0): number => {
      if (!nodes.length) return 0;

      const totalChildrenWidth = nodes.reduce((total, node) => {
        const childrenWidth = node.children?.length
          ? calculatePositions(node.children, level + 1, total)
          : NODE_WIDTH;

        const width = Math.max(NODE_WIDTH, childrenWidth);

        node.position = {
          x: total + (width - NODE_WIDTH) / 2,
          y: level * LEVEL_HEIGHT,
          width: NODE_WIDTH,
          height: NODE_HEIGHT
        };

        return total + width + NODE_SPACING;
      }, 0);

      // Center children under parent
      if (parentX > 0) {
        const shift = parentX - (totalChildrenWidth - NODE_SPACING) / 2;
        nodes.forEach(node => {
          if (node.position) {
            node.position.x += shift;
          }
        });
      }

      return totalChildrenWidth;
    };

    const roots = findRootQuestions(questionsData);
    calculatePositions(roots);
    setTreeData(roots);
  }, [questionsData]);

  // Add this effect to auto-generate ID when question text changes and ID is empty
  useEffect(() => {
    if (!selectedQuestion && newQuestion.question && !newQuestion.id) {
      const generatedId = generateQuestionId(newQuestion.question);
      setNewQuestion(prev => ({
        ...prev,
        id: generatedId
      }));
    }
  }, [newQuestion.id, newQuestion.question, selectedQuestion]);

  const handleQuestionSelect = (question: Question) => {
    setSelectedQuestion(question);
    setNewQuestion({
      id: question.id,
      question: question.question,
      options: question.options
    });
    setEditDialog(true);
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      try {
        await DatabaseService.deleteQuestion(questionId);
        const response = await DatabaseService.getQuestions();
        const questions = response.questions.map(convertDatabaseQuestion);
        setQuestionsData(questions);
        alert('Question deleted successfully.');
      } catch (error) {
        console.error('Error deleting question:', error);
        alert('Failed to delete question. Please check the console for details.');
      }
    }
  };

  const handleSaveQuestion = async () => {
    try {
      const saveQuestions = async (questions: Question[]) => {
        const databaseQuestions = questions.map(convertToDatabaseQuestion);
        await DatabaseService.saveQuestions({ questions: databaseQuestions });
      };

      let updatedQuestions: Question[];
      if (selectedQuestion) {
        // Update existing question
        updatedQuestions = questionsData.map(q =>
          q.id === selectedQuestion.id ? newQuestion : q
        );
      } else {
        // Add new question
        updatedQuestions = [...questionsData, newQuestion];
      }

      // Save all questions to persist the links
      setQuestionsData(updatedQuestions);
      await saveQuestions(updatedQuestions);

      // Reset UI state
      setEditDialog(false);
      setSelectedQuestion(null);
      setNewQuestion({ id: '', question: '', options: [] });
      setLinkStart(null);
      setLinkMode(false);
    } catch (error) {
      console.error('Error saving question:', error);
      alert('Failed to save question. Please check the console for details.');
    }
  };

  const handleAddOption = () => {
    if (editingOption.text.trim()) {
      setNewQuestion({
        ...newQuestion,
        options: [...newQuestion.options, editingOption]
      });
      setEditingOption(emptyOption); // Reset to empty option
    }
  };

  const handleDeleteOption = (index: number) => {
    setNewQuestion({
      ...newQuestion,
      options: newQuestion.options.filter((_, i) => i !== index)
    });
  };

  const snapToGridPoint = (x: number, y: number) => {
    if (!snapToGrid) return { x, y };
    return {
      x: Math.round(x / GRID_SIZE) * GRID_SIZE,
      y: Math.round(y / GRID_SIZE) * GRID_SIZE
    };
  };

  const handleQuestionDrag = (id: string, deltaX: number, deltaY: number) => {
    setTreeData(prevData => {
      const updateNodePosition = (nodes: TreeNodeData[]): TreeNodeData[] => {
        return nodes.map(node => {
          if (node.id === id && node.position) {
            // Get the SVG element and its CTM
            const svg = svgRef.current;
            if (!svg) return node;

            // Get the CTM
            const ctm = svg.getScreenCTM();
            if (!ctm) return node;

            // Convert screen coordinates to SVG coordinates
            const svgDeltaX = deltaX / ctm.a;
            const svgDeltaY = deltaY / ctm.d;

            const newX = node.position.x + svgDeltaX;
            const newY = node.position.y + svgDeltaY;
            const { x: snappedX, y: snappedY } = snapToGridPoint(newX, newY);

            return {
              ...node,
              position: {
                ...node.position,
                x: snappedX,
                y: snappedY
              },
              children: node.children ? updateNodePosition(node.children) : undefined
            };
          }
          return {
            ...node,
            children: node.children ? updateNodePosition(node.children) : undefined
          };
        });
      };

      return updateNodePosition(prevData);
    });
  };

  const handleDeleteConnection = (sourceId: string, targetId: string) => {
    setQuestionsData(prevData => {
      const updatedData = prevData.map(question => {
        if (question.id === sourceId) {
          return {
            ...question,
            options: question.options.map(opt => ({
              ...opt,
              nextQuestion: opt.nextQuestion === targetId ? '' : opt.nextQuestion
            }))
          };
        }
        return question;
      });

      // Save to database
      const saveQuestions = async () => {
        try {
          const databaseQuestions = updatedData.map(convertToDatabaseQuestion);
          await DatabaseService.saveQuestions({ questions: databaseQuestions });
        } catch (error) {
          console.error('Error saving questions after deleting connection:', error);
          alert('Failed to save connection deletion. Please check the console for details.');
        }
      };
      saveQuestions();

      return updatedData;
    });
  };

  const renderConnections = (node: TreeNodeData) => {
    if (!node.position || !node.children?.length) return null;

    return node.children.map((child, index) => {
      if (!child.position) return null;

      const startX = node.position!.x + node.position!.width / 2;
      const startY = node.position!.y + node.position!.height;
      const endX = child.position.x + child.position.width / 2;
      const endY = child.position.y;

      // Calculate midpoint for the vertical line
      const midY = startY + (endY - startY) / 2;

      // Create orthogonal path with straight lines
      const path = `M ${startX} ${startY} 
                   L ${startX} ${midY}
                   L ${endX} ${midY}
                   L ${endX} ${endY}`;

      // Find the option text that links to this child
      const optionText = node.options.find(opt => opt.nextQuestion === child.id)?.text || '';

      return (
        <g key={`${node.id}-${child.id}-${index}`}>
          <path
            d={path}
            fill="none"
            stroke="#2E7D32"
            strokeWidth="2"
          />
          {/* Option text box at the midpoint */}
          <g transform={`translate(${(startX + endX) / 2}, ${midY})`}>
            <rect
              x="-60"
              y="-12"
              width="120"
              height="24"
              fill="white"
              stroke="#2E7D32"
              strokeWidth="1"
              rx="4"
            />
            <text
              textAnchor="middle"
              fill="#2E7D32"
              fontSize="12"
              dy="4"
            >
              {optionText}
            </text>
            {/* Delete connection button */}
            <g
              transform="translate(50, 0)"
              style={{ cursor: 'pointer' }}
              onClick={() => handleDeleteConnection(node.id, child.id)}
            >
              <circle r="8" fill="#ff4444" />
              <text
                textAnchor="middle"
                fill="white"
                fontSize="12"
                dy="4"
              >
                ×
              </text>
            </g>
          </g>
        </g>
      );
    });
  };

  const duplicateQuestionWithChildren = (
    question: Question,
    existingIds: Set<string>,
    idMapping: Map<string, string> = new Map()
  ): { question: Question; newIds: Map<string, string> } => {
    // Generate new ID for this question
    const newId = generateUniqueQuestionId(question.id, existingIds);
    existingIds.add(newId); // Add to existing IDs to prevent duplicates
    idMapping.set(question.id, newId);

    // Find all child questions (questions referenced in nextQuestion)



    // Create duplicated question with updated references
    const duplicatedQuestion: Question = {
      ...question,
      id: newId,
      options: question.options.map(opt => ({
        ...opt,
        nextQuestion: opt.nextQuestion ? idMapping.get(opt.nextQuestion) || opt.nextQuestion : '',
        recommendations: [...opt.recommendations]
      }))
    };

    return { question: duplicatedQuestion, newIds: idMapping };
  };

  const handleDuplicateQuestion = (question: Question) => {
    // Get all existing question IDs
    const existingIds = new Set(questionsData.map(q => q.id));

    // Duplicate question and all its children
    const { newIds } = duplicateQuestionWithChildren(question, existingIds);

    // Get all questions that need to be duplicated (the question and all its children)
    const allDuplicatedQuestions = Array.from(newIds.entries()).map(([oldId, newId]) => {
      const originalQuestion = questionsData.find(q => q.id === oldId);
      if (!originalQuestion) return null;

      return {
        ...originalQuestion,
        id: newId,
        options: originalQuestion.options.map(opt => ({
          ...opt,
          nextQuestion: opt.nextQuestion ? newIds.get(opt.nextQuestion) || opt.nextQuestion : '',
          recommendations: [...opt.recommendations]
        }))
      };
    }).filter((q): q is Question => q !== null);

    // Add all duplicated questions to the questions data
    const updatedQuestions = [...questionsData, ...allDuplicatedQuestions];
    setQuestionsData(updatedQuestions);

    // Save to database
    const saveQuestions = async () => {
      try {
        const databaseQuestions = updatedQuestions.map(convertToDatabaseQuestion);
        await DatabaseService.saveQuestions({ questions: databaseQuestions });
      } catch (error) {
        console.error('Error saving duplicated questions:', error);
        alert('Failed to save duplicated questions. Please check the console for details.');
      }
    };
    saveQuestions();
  };

  const renderNode = (node: TreeNodeData, parentId: string = '') => {
    if (!node.position) return null;

    // Generate a unique key that includes the full path
    const nodeKey = parentId ? `${parentId}-${node.id}` : `root-${node.id}`;

    // If this is a reference node, render it differently
    if (node.isReference) {
      return (
        <g key={nodeKey}>
          <DraggableTreeNode
            id={node.id}
            question={node.question}
            options={node.options}
            onEdit={() => handleQuestionSelect(node)}
            onDelete={() => handleDeleteQuestion(node.id)}
            onDuplicate={() => handleDuplicateQuestion(node)}
            position={node.position}
            onDrag={handleQuestionDrag}
            onClick={() => handleQuestionClick(node.id)}
            isLinking={linkMode}
            isLinkStart={linkStart === node.id}
            isReference={true}
          />
          {renderConnections(node)}
        </g>
      );
    }

    return (
      <g key={nodeKey}>
        <DraggableTreeNode
          id={node.id}
          question={node.question}
          options={node.options}
          onEdit={() => handleQuestionSelect(node)}
          onDelete={() => handleDeleteQuestion(node.id)}
          onDuplicate={() => handleDuplicateQuestion(node)}
          position={node.position}
          onDrag={handleQuestionDrag}
          onClick={() => handleQuestionClick(node.id)}
          isLinking={linkMode}
          isLinkStart={linkStart === node.id}
        />
        {renderConnections(node)}
        {node.children?.map((child, index) => renderNode(child, `${nodeKey}-${index}`))}
      </g>
    );
  };

  // Calculate SVG dimensions based on tree data
  const calculateSvgDimensions = () => {
    let maxX = 0;
    let maxY = 0;

    const processNode = (node: TreeNodeData) => {
      if (node.position) {
        const rightEdge = node.position.x + node.position.width + NODE_SPACING;
        const bottomEdge = node.position.y + node.position.height + LEVEL_HEIGHT;
        maxX = Math.max(maxX, rightEdge);
        maxY = Math.max(maxY, bottomEdge);
      }
      node.children?.forEach(processNode);
    };

    treeData.forEach(processNode);

    // Add padding and ensure minimum dimensions
    const width = Math.max(maxX + PADDING * 2, 1200);
    const height = Math.max(maxY + PADDING * 2, 800);

    return { width, height };
  };

  const { width: svgWidth, height: svgHeight } = calculateSvgDimensions();

  // Add function to handle linking questions
  const handleQuestionClick = (questionId: string) => {
    if (!linkMode) return;

    if (!linkStart) {
      setLinkStart(questionId);
    } else {
      // Create link between questions
      const sourceQuestion = questionsData.find(q => q.id === linkStart);
      if (sourceQuestion && questionId !== linkStart) {
        setEditDialog(true);
        setSelectedQuestion(sourceQuestion);
        setNewQuestion({
          ...sourceQuestion,
          options: [
            ...sourceQuestion.options,
            { text: 'New Option', nextQuestion: questionId, recommendations: [] }
          ]
        });
      }
      setLinkStart(null);
      setLinkMode(false);
    }
  };

  // Add grid rendering function
  const renderGrid = () => {
    if (!snapToGrid) return null;

    const gridLines = [];
    const width = Math.max(svgWidth, 1200);
    const height = Math.max(svgHeight, 800);

    // Vertical lines
    for (let x = 0; x <= width; x += GRID_SIZE) {
      gridLines.push(
        <line
          key={`v-${x}`}
          x1={x}
          y1={0}
          x2={x}
          y2={height}
          stroke={GRID_COLOR}
          strokeWidth="1"
        />
      );
    }

    // Horizontal lines
    for (let y = 0; y <= height; y += GRID_SIZE) {
      gridLines.push(
        <line
          key={`h-${y}`}
          x1={0}
          y1={y}
          x2={width}
          y2={y}
          stroke={GRID_COLOR}
          strokeWidth="1"
        />
      );
    }

    return <g>{gridLines}</g>;
  };

  const handleExportData = async () => {
    try {
      // Export questions and products separately using available methods
      const questions = await DatabaseService.getQuestions();
      const products = await DatabaseService.getProducts();

      const data = {
        questions,
        products
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'survey-data.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
      // TODO: Add error notification
    }
  };

  const handleImportQuestions = async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'application/json';

      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = async (e) => {
            try {
              // Import questions
              const fileData = JSON.parse(reader.result as string);
              console.log('Parsed file data:', fileData);

              // Try to extract questions array from various possible structures
              const questions = fileData.questions?.questions || fileData.questions || fileData;
              console.log('Extracted questions:', questions);

              if (!Array.isArray(questions)) {
                throw new Error('Questions must be an array in import file');
              }

              // Validate each question has required fields
              questions.forEach((q: any, i: number) => {
                if (!q.id || !q.question || !Array.isArray(q.options)) {
                  throw new Error(`Invalid question format at index ${i}`);
                }
                // Validate each option
                q.options.forEach((opt: any, j: number) => {
                  if (!opt.text) {
                    throw new Error(`Invalid option format at question ${i}, option ${j}`);
                  }
                });
              });

              // Wrap questions in the expected structure
              const formattedData = { questions };
              console.log('Sending to DatabaseService:', formattedData);

              await DatabaseService.saveQuestions(formattedData);

              // Refresh the data
              const response = await DatabaseService.getQuestions();
              const convertedQuestions = response.questions.map(convertDatabaseQuestion);
              setQuestionsData(convertedQuestions);

              alert('Questions imported successfully!');
            } catch (error) {
              console.error('Error importing questions:', error);
              alert(`Failed to import questions: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
          };
          reader.readAsText(file);
        }
      };

      input.click();
    } catch (error) {
      console.error('Error importing questions:', error);
      alert(`Failed to import questions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleImportProducts = async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'application/json';

      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = async (e) => {
            try {
              const content = e.target?.result as string;
              const data = JSON.parse(content);

              // Validate imported data structure
              if (!data || typeof data !== 'object') {
                throw new Error('Invalid import file format');
              }

              // Import products
              const products = data.products || data;
              if (!Array.isArray(products)) {
                throw new Error('Products must be an array');
              }

              // Validate and import each product
              for (const product of products) {
                if (!product.id || !product.name || !product.description || typeof product.price !== 'number') {
                  throw new Error(`Invalid product format: ${JSON.stringify(product)}`);
                }
                await DatabaseService.saveProduct(product);
              }

              alert('Products imported successfully!');
            } catch (error) {
              console.error('Error importing products:', error);
              alert(`Failed to import products: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
          };
          reader.readAsText(file);
        }
      };

      input.click();
    } catch (error) {
      console.error('Error importing products:', error);
      alert(`Failed to import products: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const adjustNodePositions = (nodes: TreeNodeData[]) => {
    // First, organize nodes by levels
    const levelNodes = new Map<number, TreeNodeData[]>();

    const processNode = (node: TreeNodeData, level: number) => {
      if (!node.position) return;

      if (!levelNodes.has(level)) {
        levelNodes.set(level, []);
      }
      levelNodes.get(level)!.push(node);

      node.children?.forEach(child => processNode(child, level + 1));
    };

    nodes.forEach(node => processNode(node, 0));

    // Adjust positions level by level
    levelNodes.forEach((nodesInLevel, level) => {
      const totalWidth = nodesInLevel.reduce((sum, node) =>
        sum + (node.position?.width || 0), 0);
      const totalSpacing = (nodesInLevel.length - 1) * MIN_NODE_SPACING;
      const levelWidth = totalWidth + totalSpacing;

      // Center the nodes in this level
      let currentX = PADDING + (svgWidth - levelWidth) / 2;
      nodesInLevel.forEach(node => {
        if (node.position) {
          node.position.x = currentX;
          node.position.y = PADDING + level * LEVEL_SPACING;
          currentX += node.position.width + MIN_NODE_SPACING;
        }
      });
    });
  };



  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Container maxWidth="xl" sx={{
      py: { xs: 2, sm: 4, md: '60px' },
      px: { xs: '8px !important', sm: '24px !important', md: '90px !important' },
      backgroundColor: 'rgba(230, 232, 232, 0.7)',
      minWidth: 0,
      overflowX: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{
        mb: { xs: 2, sm: 4 },
        display: 'flex',
        flexDirection: { xs: 'row', sm: 'row' },
        justifyContent: { xs: 'space-between', sm: 'space-between' },
        alignItems: { xs: 'center', sm: 'center' },
        gap: { xs: 0, sm: 0 },
      }}>
        <Box
          component="img"
          src={logoTachafy}
          alt="Tachafy Logo"
          sx={{
            width: { xs: '120px', sm: '157px' },
            height: { xs: '22px', sm: '26px' },
            borderRadius: '6px',
            cursor: 'pointer',
            mb: { xs: 0, sm: 0 },
          }}
          onClick={() => navigate('/')}
        />
        <Box sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          flexDirection: { xs: 'row', sm: 'row' },
          width: { xs: 'auto', sm: 'auto' },
          alignItems: 'center',
        }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              py: { xs: 0.5, sm: 1 },
              px: { xs: 1, sm: 2 },
              fontSize: { xs: '0.8rem', sm: '1rem' },
              minHeight: { xs: '30px', sm: '40px' },
              borderRadius: { xs: '4px', sm: '8px' },
              minWidth: { xs: 90, sm: 120 },
              width: 'auto',
              flexShrink: 0,
            }}
            fullWidth={false}
          >
            Logout
          </Button>
        </Box>
      </Box>


      {/* Tabs */}
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons={isMobile ? 'auto' : undefined}
        sx={{
          mb: { xs: 1, sm: 2 }, borderBottom: 1, borderColor: 'divider',
          '.MuiTab-root': {
            minWidth: 0,
            px: { xs: 1, sm: 2 },
            fontSize: { xs: '0.9rem', sm: '1.2rem', md: '25px' },
          },
        }}
      >
        {[
          { icon: <PeopleAltOutlinedIcon />, label: 'DoctorsSection', id: 'admin-tab-5' },
          { icon: <RateReviewOutlinedIcon />, label: 'Reviews List', id: 'admin-tab-6' },
          { icon: <BarChartOutlinedIcon />, label: 'Statistics', id: 'admin-tab-7' },
        ].map((tab) => (
          <Tab
            key={tab.id}
            id={tab.id}
            icon={tab.icon}
            iconPosition={tab.icon ? 'start' : undefined}
            label={tab.label}
            sx={{
              fontFamily: 'Manrope',
              fontSize: '25px',
              fontWeight: 500,
              color: 'rgba(2, 7, 66, 0.6)',
              '&.Mui-selected': {
                fontWeight: 600,
                color: '#020742',
              },
            }}
          />
        ))}
      </Tabs>

      {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
         <ImportDataButton />
       </Box> */}

      <TabPanel value={currentTab} index={3}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: { xs: 2, sm: 3 },
          gap: { xs: 2, md: 0 },
        }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }, mb: { xs: 1, sm: 0 } }}>Survey Question Tree</Typography>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 },
            width: { xs: '100%', sm: 'auto' },
          }}>
            <Button
              variant="contained"
              
              color={snapToGrid ? "secondary" : "primary"}
              onClick={() => setSnapToGrid(!snapToGrid)}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              {snapToGrid ? "Disable Grid" : "Enable Grid"}
            </Button>
            <Button
              variant="contained"
              color={linkMode ? "secondary" : "primary"}
              startIcon={<LinkIcon />}
              onClick={() => {
                setLinkMode(!linkMode);
                setLinkStart(null);
              }}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              {linkMode ? "Cancel Linking" : "Link Questions"}
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportData}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              Export All Data
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleImportQuestions}
              color="primary"
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              Import Questions
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleImportProducts}
              color="secondary"
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              Import Products
            </Button>
            <Button
              variant="contained"
              startIcon={<ZoomOutIcon />}
              onClick={() => setScale(prev => Math.max(0.5, prev - 0.1))}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              Zoom Out
            </Button>
            <Button
              variant="contained"
              startIcon={<ZoomInIcon />}
              onClick={() => setScale(prev => Math.min(2, prev + 0.1))}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              Zoom In
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedQuestion(null);
                setNewQuestion({
                  id: '',
                  question: '',
                  options: []
                });
                setEditDialog(true);
              }}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              Add Question
            </Button>
          </Box>
        </Box>

        <Paper
          sx={{
            p: { xs: 1, sm: 2 },
            minHeight: { xs: '60vh', sm: '80vh', md: '100vh' },
            height: { xs: '60vh', sm: '80vh', md: '100vh' },
            overflow: 'hidden',
            width: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden',
          }}
        >
          <Box sx={{
            width: '100%',
            height: '100%',
            overflow: 'auto',
            position: 'relative',
            minWidth: 0,
            overflowX: 'hidden',
          }}>
            <Box
              sx={{
                transform: `scale(${scale})`,
                transformOrigin: '0 0',
                margin: { xs: '4px', sm: '10px', md: '20px' },
                width: '100%',
                maxWidth: '100%',
                height: svgHeight,
                minWidth: 0,
                overflowX: 'hidden',
              }}
            >
              <svg
                ref={svgRef}
                width="100%"
                height={svgHeight}
                style={{
                  background: 'white',
                  cursor: linkMode ? 'crosshair' : 'grab',
                  display: 'block',
                  minWidth: 0,
                  maxWidth: '100%',
                  overflowX: 'hidden',
                }}
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                preserveAspectRatio="xMinYMin meet"
              >
                {renderGrid()}
                <g transform={`translate(${PADDING}, ${PADDING})`}>
                  {treeData.map((node, index) => {
                    adjustNodePositions(treeData);
                    return (
                      <g key={`root-${index}-${node.id}`}>
                        {renderNode(node)}
                      </g>
                    );
                  })}
                </g>
              </svg>
            </Box>
          </Box>
        </Paper>

        <Dialog
          open={editDialog}
          onClose={() => setEditDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              width: { xs: '98%', sm: '90%', md: '80%' },
              maxHeight: { xs: '95vh', sm: '80vh' },
              overflowY: 'auto',
              m: { xs: 0.5, sm: 2 },
            },
          }}
        >
          <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
            {selectedQuestion ? 'Edit Question' : 'Add New Question'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Question ID"
                value={newQuestion.id}
                onChange={(e) => setNewQuestion({ ...newQuestion, id: e.target.value })}
                sx={{ mb: 2 }}
                helperText="ID will be auto-generated based on question text, but you can modify it"
                size={isMobile ? 'small' : 'medium'}
              />
              <TextField
                fullWidth
                label="Question Text"
                value={newQuestion.question}
                onChange={(e) => {
                  const newText = e.target.value;
                  setNewQuestion(prev => ({
                    ...prev,
                    question: newText,
                    id: (!selectedQuestion && !prev.id) ? generateQuestionId(newText) : prev.id
                  }));
                }}
                sx={{ mb: 3 }}
                size={isMobile ? 'small' : 'medium'}
              />

              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                Options
              </Typography>
              <List sx={{
                maxHeight: { xs: '150px', sm: '200px', md: '300px' },
                overflowY: 'auto',
                border: '1px solid #eee',
                borderRadius: 1,
                mb: 2,
              }}>
                {newQuestion.options.map((option, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={option.text}
                      secondary={
                        <Typography component="div" variant="body2" color="textSecondary">
                          <Box>
                            {option.nextQuestion && (
                              <Chip
                                size="small"
                                label={`Next: ${option.nextQuestion}`}
                                color="primary"
                                sx={{ mr: 1, mb: 0.5 }}
                              />
                            )}
                            {option.recommendations?.map((rec, i) => (
                              <Chip
                                key={i}
                                size="small"
                                label={rec}
                                color="secondary"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => {
                          setEditingOption({ ...option, index });
                          setIsEditingExistingOption(true);
                        }}
                        sx={{ mr: 1 }}
                        size={isMobile ? "small" : "medium"}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteOption(index)}
                        size={isMobile ? "small" : "medium"}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                {isEditingExistingOption ? 'Edit Option' : 'Add Option'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Option Text"
                    value={editingOption.text}
                    onChange={(e) =>
                      setEditingOption({
                        ...editingOption,
                        text: e.target.value
                      })
                    }
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    freeSolo
                    options={questionsData
                      .map(q => q.id)
                      .filter(id => id !== newQuestion.id) // Exclude current question to prevent self-reference
                    }
                    value={editingOption.nextQuestion || ''}
                    onChange={(_, newValue) =>
                      setEditingOption({
                        ...editingOption,
                        nextQuestion: newValue || ''
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Next Question ID (optional)"
                        onChange={(e) =>
                          setEditingOption({
                            ...editingOption,
                            nextQuestion: e.target.value
                          })
                        }
                        size={isMobile ? "small" : "medium"}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body1">{option}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {questionsData.find(q => q.id === option)?.question || ''}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Recommendations (comma-separated)"
                    value={editingOption.recommendations?.join(', ') || ''}
                    onChange={(e) =>
                      setEditingOption({
                        ...editingOption,
                        recommendations: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                      })
                    }
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 2 },
                  }}>
                    <Button
                      variant="outlined"
                      startIcon={isEditingExistingOption ? <EditIcon /> : <AddIcon />}
                      onClick={() => {
                        if (isEditingExistingOption && typeof editingOption.index === 'number') {
                          // Update existing option
                          const updatedOptions = [...newQuestion.options];
                          updatedOptions[editingOption.index] = {
                            text: editingOption.text,
                            nextQuestion: editingOption.nextQuestion || '',
                            recommendations: editingOption.recommendations || []
                          };
                          setNewQuestion({
                            ...newQuestion,
                            options: updatedOptions
                          });
                          // Reset form
                          setEditingOption(emptyOption);
                          setIsEditingExistingOption(false);
                        } else {
                          // Add new option
                          handleAddOption();
                        }
                      }}
                      disabled={!editingOption.text.trim()}
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        ...(isMobile ? {
                          py: 0.5,
                          px: 1.5,
                          fontSize: '0.85rem',
                          borderRadius: 2,
                          minHeight: '32px',
                          minWidth: 0,
                          lineHeight: 1.2,
                        } : {}),
                      }}
                    >
                      {isEditingExistingOption ? 'Update Option' : 'Add Option'}
                    </Button>
                    {isEditingExistingOption && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => {
                          setEditingOption(emptyOption);
                          setIsEditingExistingOption(false);
                        }}
                        fullWidth
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          ...(isMobile ? {
                            py: 0.5,
                            px: 1.5,
                            fontSize: '0.85rem',
                            borderRadius: 2,
                            minHeight: '32px',
                            minWidth: 0,
                            lineHeight: 1.2,
                          } : {}),
                        }}
                      >
                        Cancel Edit
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{
            p: { xs: 1, sm: 2, md: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
          }}>
            <Button
              onClick={() => setEditDialog(false)}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                ...(isMobile ? {
                  py: 0.5,
                  px: 1.5,
                  fontSize: '0.85rem',
                  borderRadius: 2,
                  minHeight: '32px',
                  minWidth: 0,
                  lineHeight: 1.2,
                } : {}),
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveQuestion}
              disabled={!newQuestion.id || !newQuestion.question}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                ...(isMobile ? {
                  py: 0.5,
                  px: 1.5,
                  fontSize: '0.85rem',
                  borderRadius: 2,
                  minHeight: '32px',
                  minWidth: 0,
                  lineHeight: 1.2,
                } : {}),
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {currentTab === 0 && (
        <TabPanel value={currentTab} index={0}>
          <DoctorSection />
        </TabPanel>
      )}
      {currentTab === 1 && (
        <TabPanel value={currentTab} index={1}>
          <ReviewsTabs />
        </TabPanel>
      )}
      {currentTab === 2 && (
        <TabPanel value={currentTab} index={2}>
          <StatisticsTabs />
        </TabPanel>
      )}
    </Container>
  );
};


export default ModelTesting; 